import React, { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MarkDownText from "../MarkDownText/MarkDownText";

const PropuestaDetalleModal = ({
  open,
  onClose,
  markdown,
  labels = [],
  itemProyecto,
  color = "#336B87",
  titulo = "Detalle de la propuesta",
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [tabIndex, setTabIndex] = useState(0);

  const scrollContainerRef = useRef(null);
  const sectionRefs = useRef({});

  const secciones = useMemo(() => {
    if (!itemProyecto || !labels.length) {
      return [
        {
          id: "general",
          label: "Contenido completo",
          contenido: markdown,
        },
      ];
    }

    return labels
      .map((labelKey, index) => {
        const valor = itemProyecto[labelKey];
        if (!valor) return null;

        const nombreBonito =
          labelKey
            ?.replace(/_/g, " ")
            ?.replace(/\b\w/g, (c) => c.toUpperCase()) || `Etapa ${index + 1}`;

        return {
          id: labelKey,
          label: nombreBonito,
          contenido: valor,
        };
      })
      .filter(Boolean);
  }, [itemProyecto, labels, markdown]);

  const handleChangeTab = (event, newIndex) => {
    setTabIndex(newIndex);

    const sectionId = secciones[newIndex]?.id;
    const el = sectionRefs.current[sectionId];

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };


  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          fontFamily: "Montserrat",
        },
      }}
    >
      {/* Header con título y botón de cerrar */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "#F5F7FA",
          borderBottom: "1px solid #E0E4EC",
          fontFamily: "Montserrat",
          fontWeight: 700,
          color: "#336B87",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Montserrat",
            fontWeight: 700,
            fontSize: { xs: "1rem", md: "1.2rem" },
          }}
        >
          {titulo}
        </Typography>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#336B87",
            "&:hover": { bgcolor: "rgba(51,107,135,0.08)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs de etapas */}
      <Box
        sx={{
          borderBottom: "1px solid #E0E4EC",
          bgcolor: "#ffffff",
          px: { xs: 1.5, md: 3 },
          pt: 1,
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          textColor="inherit"
          TabIndicatorProps={{
            sx: { backgroundColor: color },
          }}
          sx={{
            "& .MuiTab-root": {
              fontFamily: "Montserrat",
              textTransform: "none",
              fontSize: { xs: 12, md: 14 },
              minHeight: 48,
              color: "#5A6B7B",
            },
            "& .Mui-selected": {
              color: color,
              fontWeight: 700,
            },
          }}
        >
          {secciones.map((sec, index) => (
            <Tab key={sec.id || index} label={sec.label} />
          ))}
        </Tabs>
      </Box>

      {/* Contenido scrollable */}
      <DialogContent
        dividers
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: "#F8FAFC",
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            maxHeight: fullScreen ? "calc(100vh - 140px)" : "70vh",
            overflowY: "auto",
            pr: { xs: 1, md: 2 },
          }}
        >
          {secciones.map((sec, index) => (
            <Box
              key={sec.id || index}
              ref={(el) => {
                if (sec.id) sectionRefs.current[sec.id] = el;
              }}
              sx={{
                scrollMarginTop: { xs: "88px", md: "110px" },

                mb: 4,
                p: { xs: 1.5, md: 2 },
                borderRadius: 3,
                bgcolor: "#ffffff",
                boxShadow: "0 4px 10px rgba(0,0,0,0.04)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 700,
                  color: color,
                  mb: 1.5,
                  fontSize: { xs: 14, md: 16 },
                }}
              >
                {sec.label}
              </Typography>

              <MarkDownText data={sec.contenido} img={null} />
            </Box>
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PropuestaDetalleModal;
